import {
  Typography,
  Box,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material";
import StandardButton from "../../../../components/StandardButton";
import React from "react";
import { nanoid } from "nanoid";

import IngredientEditDialog from "./IngredientEditDialog";
import useIngredients from "../../../../hooks/useIngredients";

import MightyDataGrid from "../../../../components/organisms/MightyDataGrid";
import useDataGridActions from "../../../../hooks/useDataGridActions";
import useEvents from "../../../../hooks/useEvents";
import { getChatGPTResponse } from "../../../../data/GlobalData";
import { IngredientNutritionMessage } from "../../../../data/ChatGPTMessages";
import {
  suggestCategory,
  SHOPPING_CATEGORY_OPTIONS,
} from "../../../../data/ShoppingCategories";
import { consola } from "consola";
import EmptyIngredients from "./EmptyIngredients";

const EditIngredients = () => {
  const { ingredients, ingredientsLoading, editIngredient } = useIngredients();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const events = useEvents();
  const { createIngredientActions } = useDataGridActions();
  const [aiLoadingIngredients, setAiLoadingIngredients] = React.useState(
    new Set(),
  );
  const [toastMessage, setToastMessage] = React.useState("");
  const [toastOpen, setToastOpen] = React.useState(false);
  const [toastSeverity, setToastSeverity] = React.useState("info");
  const selected = React.useRef();

  const onEdit = React.useCallback((row) => {
    selected.current = row;
    setDialogOpen(true);
  }, []);

  const onAINutrition = React.useCallback(
    async (ingredient) => {
      if (!ingredient.name || !ingredient.unit) {
        setToastMessage(
          "Ingredient missing name or unit for AI nutrition calculation",
        );
        setToastSeverity("warning");
        setToastOpen(true);
        return;
      }

      consola.log("Starting AI nutrition for ingredient:", ingredient.id);

      // Show loading toast
      setToastMessage(`Getting nutrition data for ${ingredient.name}...`);
      setToastSeverity("info");
      setToastOpen(true);

      // Add ingredient to loading state
      setAiLoadingIngredients((prev) => {
        const newSet = new Set(prev).add(ingredient.id);
        consola.log("Updated loading state:", newSet);
        return newSet;
      });

      try {
        const messages = IngredientNutritionMessage({
          ingredientName: ingredient.name,
          unit: ingredient.unit,
        });

        const response = await getChatGPTResponse({
          messages: messages,
          temperature: 0.1,
        });

        const nutritionData = JSON.parse(response.response[0].message.content);

        // Validate the response structure
        if (
          typeof nutritionData.protein === "number" &&
          typeof nutritionData.carbs === "number" &&
          typeof nutritionData.fats === "number"
        ) {
          // Update the ingredient with AI-generated nutrition data
          const updatedIngredient = {
            ...ingredient,
            proteins: nutritionData.protein,
            carbs: nutritionData.carbs,
            fats: nutritionData.fats,
            category:
              nutritionData.category || suggestCategory(ingredient.name),
          };

          await editIngredient(updatedIngredient);

          // Show success toast
          setToastMessage(
            `Updated ${ingredient.name}: P=${nutritionData.protein}g, C=${nutritionData.carbs}g, F=${nutritionData.fats}g`,
          );
          setToastSeverity("success");
          setToastOpen(true);

          consola.log(
            `Updated nutrition for ${ingredient.name}: P=${nutritionData.protein}g, C=${nutritionData.carbs}g, F=${nutritionData.fats}g`,
          );
        } else {
          setToastMessage("Invalid nutrition data format from AI");
          setToastSeverity("error");
          setToastOpen(true);
          consola.error("Invalid nutrition data format from AI");
        }
      } catch (error) {
        setToastMessage(`Error getting AI nutrition data: ${error.message}`);
        setToastSeverity("error");
        setToastOpen(true);
        consola.error("Error getting AI nutrition data:", error);
      } finally {
        // Remove ingredient from loading state
        setAiLoadingIngredients((prev) => {
          const newSet = new Set(prev);
          newSet.delete(ingredient.id);
          consola.log(
            "Clearing loading state for:",
            ingredient.id,
            "New set:",
            newSet,
          );
          return newSet;
        });
      }
    },
    [editIngredient],
  );

  const columns = React.useMemo(() => {
    return [
      {
        field: "name",
        headerName: "Ingredient Name",
        minWidth: 100,
        flex: 1,
        hideable: false,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const isLoading = aiLoadingIngredients.has(params.row.id);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span>{params.row.name}</span>
              {isLoading && (
                <CircularProgress
                  size={16}
                  thickness={4}
                  sx={{ color: "primary.main" }}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: "proteins",
        headerName: "Proteins (g)",
        minWidth: 100,
        flex: 1,
        hideable: false,
        headerAlign: "left",
        align: "left",
        valueGetter: (params) => {
          const value = params.row.proteins;
          if (!value) {
            return 0;
          }
          return value;
        },
      },
      {
        field: "carbs",
        headerName: "Carbs (g)",
        minWidth: 100,
        flex: 1,
        hideable: false,
        headerAlign: "left",
        align: "left",
        valueGetter: (params) => {
          const value = params.row.carbs;
          if (!value) {
            return 0;
          }
          return value;
        },
      },
      {
        field: "fats",
        headerName: "Fats (g)",
        minWidth: 100,
        flex: 1,
        hideable: false,
        headerAlign: "left",
        align: "left",
        valueGetter: (params) => {
          const value = params.row.fats;
          if (!value) {
            return 0;
          }
          return value;
        },
      },
      {
        field: "unit",
        headerName: "per unit",
        minWidth: 100,
        flex: 1,
        hideable: false,
        headerAlign: "left",
        align: "left",
        valueGetter: (params) => {
          const value = params.row.unit;
          if (!value) {
            return "-";
          }
          return value;
        },
      },
      {
        field: "category",
        headerName: "Category",
        minWidth: 120,
        flex: 1,
        hideable: false,
        headerAlign: "left",
        align: "left",
        valueGetter: (params) => {
          const value = params.row.category;
          if (!value) {
            return "Other";
          }
          // Display the readable category name
          const categoryOption = SHOPPING_CATEGORY_OPTIONS.find(
            (opt) => opt.value === value,
          );
          return categoryOption ? categoryOption.label : value;
        },
      },
    ];
  }, [aiLoadingIngredients]);

  // Move this hook to the top level to avoid conditional hook calls
  const filteredIngredients = React.useMemo(
    () =>
      ingredients?.filter((ingredient) =>
        ingredient.name.toLowerCase().includes(searchTerm.toLowerCase()),
      ) || [],
    [ingredients, searchTerm],
  );

  // Custom actions function for MightyDataGrid
  const customActions = React.useCallback(
    (row) => {
      const isLoading = aiLoadingIngredients.has(row.id);

      return createIngredientActions({
        onEdit: (item) => {
          events.logButtonClick({
            buttonName: "Edit Ingredient",
            pageName: "Nutrition",
            tabName: "Ingredients",
          });
          onEdit(item);
        },
        onAINutrition: (item) => {
          events.logButtonClick({
            buttonName: "AI Fill Nutrition",
            pageName: "Nutrition",
            tabName: "Ingredients",
          });
          onAINutrition(item);
        },
        entityName: "Ingredient",
        isAiLoading: isLoading,
      });
    },
    [
      aiLoadingIngredients,
      createIngredientActions,
      events,
      onEdit,
      onAINutrition,
    ],
  );

  return (
    <>
      {/* Show loading first, then empty state when data confirms no ingredients */}
      {ingredientsLoading ? (
        <Typography>Loading ingredients...</Typography>
      ) : !ingredients || ingredients.length === 0 ? (
        <EmptyIngredients
          onAddIngredient={() => {
            selected.current = null;
            setDialogOpen(true);
          }}
        />
      ) : (
        <>
          <Grid container style={{ marginTop: 0 }}>
            <Grid item xs={8}>
              <Typography variant="h6">Ingredients</Typography>
              <Typography
                style={{ fontSize: 16, fontWeight: "normal", marginBottom: 12 }}
              >
                This your ingredient database. Setup ingredients you will use in
                the recipes you create.
              </Typography>
            </Grid>

            <Grid item xs={4}>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent={"flex-end"}
              >
                <StandardButton
                  style={{ marginTop: 12 }}
                  variant="primary"
                  onClick={() => {
                    selected.current = null;
                    setDialogOpen(true);
                    events.logButtonClick({
                      buttonName: "Add Ingredient",
                      pageName: "Nutrition",
                      tabName: "Ingredients",
                    });
                  }}
                >
                  Add Ingredient
                </StandardButton>
              </Box>
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" mt={2} mb={2}>
            <TextField
              placeholder="Search ingredients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">🔍</InputAdornment>
                ),
              }}
            />
          </Box>

          <MightyDataGrid
            entityName="ingredients"
            columns={columns}
            rows={filteredIngredients}
            showActions={true}
            actions={customActions}
          />
        </>
      )}

      {/* Dialog and Toast - Always rendered regardless of empty state */}

      {dialogOpen && (
        <IngredientEditDialog
          open={dialogOpen}
          ingredient={selected.current}
          onDialogClosed={async (data, ingredient) => {
            if (!data && !ingredient) {
              setDialogOpen(false);
              return;
            }
            setDialogOpen(false);

            if (data && ingredient) {
              ingredient.name = data.name;
              ingredient.proteins = data.proteins;
              ingredient.carbs = data.carbs;
              ingredient.fats = data.fats;
              ingredient.unit = data.unit;
              ingredient.category = data.category;

              editIngredient(ingredient);
            } else {
              const newIngredient = {
                id: nanoid(),
                name: data.name,
                proteins: data.proteins,
                carbs: data.carbs,
                fats: data.fats,
                unit: data.unit,
                category: data.category,
              };

              editIngredient(newIngredient);
            }
          }}
        />
      )}

      {/* Toast notifications */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditIngredients;
